import { Helmet } from 'react-helmet-async';
import { useEffect } from 'react';
import { SITE_URL } from '@/config/brand';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogTitle?: string;
  ogType?: 'website' | 'article' | 'product';
  noIndex?: boolean;
  structuredData?: object;
}

export const SEOHead = ({
  title = 'NAT HOF | Dermocosméticos para Pele Sensibilizada',
  description = 'Dermocosméticos desenvolvidos para peles em contextos específicos: oncologia, melasma, pós-procedimentos, gestação e mais. Tecnologia de barreira com ingredientes seguros.',
  keywords = 'dermocosmético, pele sensibilizada, barreira cutânea, oncologia, melasma, gestação, pós-procedimento',
  canonicalUrl,
  ogImage = `${SITE_URL}/og-image.png`,
  ogTitle,
  ogType = 'website',
  noIndex = false,
  structuredData,
}: SEOHeadProps) => {
  const fullTitle = title.includes('NAT HOF') ? title : `${title} | NAT HOF`;
  const fullOgTitle = ogTitle || fullTitle;
  // Always derive canonical from SITE_URL + pathname, never from window.location (which may be preview).
  // Pages may pass a site-relative path ("/bioativos/x"); a relative canonical is resolved by Google but
  // is ambiguous for other consumers, so make it absolute here (one place, every page).
  const url = canonicalUrl
    ? (canonicalUrl.startsWith('/') ? `${SITE_URL}${canonicalUrl}` : canonicalUrl)
    : (typeof window !== 'undefined' ? `${SITE_URL}${window.location.pathname}` : SITE_URL);

  // When a route opts into noIndex (e.g. 404 / soft-404 pages), strip the static
  // `<meta name="robots" content="index, follow">` shipped in index.html so the
  // page ends up with a SINGLE noindex directive instead of two conflicting tags.
  useEffect(() => {
    if (!noIndex || typeof document === 'undefined') return;
    const staticRobots = document.querySelectorAll('meta[name="robots"]:not([data-rh])');
    staticRobots.forEach((el) => el.parentElement?.removeChild(el));
  }, [noIndex]);

  // The prerender (scripts/prerender.mjs) and the inline script in index.html ship static copies of
  // canonical / description / og:* / twitter:* for non-JS crawlers. Helmet adds its own (data-rh) copies
  // with the page-authored values, so a JS-rendering crawler saw two different descriptions and up to
  // three canonicals per page. Once Helmet has rendered, drop the static copies: one value per tag.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const raf = requestAnimationFrame(() => {
      document.head
        .querySelectorAll(
          'link[rel="canonical"]:not([data-rh]), meta[name="description"]:not([data-rh]), meta[property^="og:"]:not([data-rh]), meta[name^="twitter:"]:not([data-rh])',
        )
        .forEach((el) => el.remove());
    });
    return () => cancelAnimationFrame(raf);
  }, [fullTitle, description, url, ogImage, fullOgTitle, ogType]);


  return (
    <Helmet>
      {/* Basic Meta */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      {noIndex && <meta name="robots" content="noindex, follow" />}
      
      {/* Canonical */}
      <link rel="canonical" href={url} />
      
      {/* Open Graph */}
      <meta property="og:title" content={fullOgTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="NAT HOF" />
      <meta property="og:locale" content="pt_BR" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullOgTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

// Organization Schema
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'NAT HOF',
  url: SITE_URL,
  logo: `${SITE_URL}/assets/logo-nathof.png`,
  description: 'Dermocosméticos desenvolvidos para peles em contextos específicos',
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'rafael@nathof.com.br',
    contactType: 'customer service',
    availableLanguage: 'Portuguese',
  },
  sameAs: [
    'https://instagram.com/nathofbr',
  ],
};

// WebSite Schema (sitewide)
export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'NAT HOF Skin Science',
  url: SITE_URL,
  description: 'Dermocosméticos para pele sensível com origem clínica e validação científica',
};

// SoftwareApplication Schema — Consulta Guiada (Quiz)
export const softwareApplicationSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Consulta Guiada NAT HOF',
  applicationCategory: 'HealthApplication',
  operatingSystem: 'Web',
  description:
    'Diagnóstico gratuito de pele sensível com índice de hidratação personalizado e rotina de cuidados baseada em ciência.',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'BRL',
  },
  provider: {
    '@type': 'Organization',
    name: 'NAT HOF Skin Science',
    url: SITE_URL,
  },
};
export const generateProductSchema = (product: {
  id: string;
  name: string;
  description: string;
  price: number;
  image: string;
  sku?: string;
  availability?: 'InStock' | 'OutOfStock';
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: product.name,
  description: product.description,
  image: product.image,
  sku: product.sku || product.id,
  brand: {
    '@type': 'Brand',
    name: 'NAT HOF',
  },
  offers: {
    '@type': 'Offer',
    price: product.price.toFixed(2),
    priceCurrency: 'BRL',
    availability: `https://schema.org/${product.availability || 'InStock'}`,
    seller: {
      '@type': 'Organization',
      name: 'NAT HOF',
    },
  },
});

// Breadcrumb Schema Generator
export const generateBreadcrumbSchema = (items: { name: string; url: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});

// FAQ Schema Generator
export const generateFAQSchema = (faqs: { question: string; answer: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

// HowTo Schema Generator
export const generateHowToSchema = (params: {
  name: string;
  description: string;
  url: string;
  steps: { name: string; text: string; url?: string }[];
  totalTime?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": params.name,
  "description": params.description,
  "url": params.url,
  "inLanguage": "pt-BR",
  "totalTime": params.totalTime || "PT10M",
  "step": params.steps.map((step, i) => ({
    "@type": "HowToStep",
    "position": i + 1,
    "name": step.name,
    "text": step.text,
    ...(step.url ? { "url": step.url } : {})
  }))
});
